import React from "react";
import { LocationInput } from "tsi-common-react/src/apps/common/containers/LocationInput";
import { useAppSelector } from "tsi-common-react/src/apps/reducers";
import { StoreSearchFilters } from "tsi-common-react/src/apps/retail/models";
import { storeFilterSelector } from "tsi-common-react/src/apps/retail/selectors";
import { ILocation } from "tsi-common-react/src/models/location";
import { IMiles, isoMiles } from "tsi-common-react/src/models/nominals";
import { t } from "ttag";

import { FilterChangeHandler } from "../models";
import { LocatorFilterPopover } from "./LocatorFilters_Popover";

const DISTANCE_OPTIONS = [10, 20, 30, 40, 50, 75, 100];
const LOCATION_INPUT_ID = "location-autocomplete";

const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
};

const getDistanceFromEvent = (
    event: React.FormEvent<HTMLSelectElement>,
): IMiles => {
    return isoMiles.wrap(parseInt(event.currentTarget.value, 10));
};

const getLabelText = (currentLocation: ILocation | null): string => {
    return (
        currentLocation?.formatted_address_long ||
        currentLocation?.formatted_address ||
        // TODO(i18n): Use t tagged template: t`you`
        "you"
    );
};

export const LocationFilter = (props: {
    filterValues: StoreSearchFilters;
    currentLocation: ILocation | null;
    onChange: FilterChangeHandler;
}): React.ReactNode => {
    const storeFilters = useAppSelector(storeFilterSelector);
    const [distanceValue, setDistanceValue] = React.useState<IMiles>(
        props.filterValues.distance,
    );
    const filterElemRef = React.useRef<{
        onRequestClose: () => void;
    }>(null);

    // Sync distance value when it changes from external source
    React.useEffect(() => {
        setDistanceValue(storeFilters.distance);
    }, [storeFilters.distance]);

    const handleDistanceChange = (
        event: React.FormEvent<HTMLSelectElement>,
    ) => {
        const newDistance = getDistanceFromEvent(event);
        setDistanceValue(newDistance);
        props.onChange({
            distance: newDistance,
        });
    };

    const handleAfterLocationChange = () => {
        if (filterElemRef.current) {
            filterElemRef.current.onRequestClose();
        }
    };

    const labelText = getLabelText(props.currentLocation);

    return (
        <LocatorFilterPopover
            ref={filterElemRef}
            id="location"
            labelText={labelText}
        >
            <form
                className="filters__dropdown filters__dropdown--location"
                onSubmit={handleSubmit}
                role="dialog"
                aria-modal={true}
            >
                <fieldset>
                    <legend className="filters__instruct">
                        {/* TODO(i18n): Use t tagged template: {t`Change your location to find retailers in that area.`} */}
                        Change your location to find retailers in that area.
                    </legend>
                    <div className="form__field filters__inputs">
                        <div className="form__input form__input--zip">
                            <label
                                htmlFor={LOCATION_INPUT_ID}
                                className="ada-screenreader-only"
                            >
                                {t`Location`}
                            </label>
                            <LocationInput
                                inputProps={{
                                    id: LOCATION_INPUT_ID,
                                    name: "bh-sl-address",
                                    // TODO(i18n): Use t tagged template: placeholder: t`Enter address, city, state or zip`
                                    placeholder:
                                        "Enter address, city, state or zip",
                                    // TODO(i18n): Use t tagged template: title: t`Enter address, city, state or zip`
                                    title: "Enter address, city, state or zip",
                                }}
                                inputFormat="long"
                                onAfterChange={handleAfterLocationChange}
                            />
                        </div>
                    </div>
                    <div className="form__field filters__inputs">
                        <div className="form__input form__input--distance">
                            <select
                                className="form__select"
                                name="bh-sl-maxdistance"
                                // TODO(i18n): Use t tagged template: aria-label={t`Max Distance`}
                                aria-label="Max Distance"
                                onChange={handleDistanceChange}
                                value={isoMiles.unwrap(distanceValue)}
                            >
                                {DISTANCE_OPTIONS.map((opt) => {
                                    return (
                                        <option key={opt} value={opt}>
                                            {/* TODO(i18n): Use t with interpolation for complete sentence: {t`${opt} miles`} */}
                                            {opt} miles
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                    <div className="filters__actions sl-search__actions al-location--submit-zip">
                        <input
                            className="button filters__button"
                            type="submit"
                            // TODO(i18n): Use t tagged template: aria-label={t`Search`}
                            aria-label="Search"
                        />
                    </div>
                </fieldset>
            </form>
        </LocatorFilterPopover>
    );
};
